
import React, { useState } from 'react';
import { ErrorBoundary } from './shared/ErrorBoundary';
import { useKYPPageState } from '../lib/kyp-search/hooks/useKYPPageState';
import { PlayerSearchPhase } from './kyp/phases/PlayerSearchPhase';
import { StaticDataPhase } from './kyp/phases/StaticDataPhase';
import { KYPSearchPhase } from './kyp/phases/KYPSearchPhase';
import { ErrorDisplay } from './kyp/phases/ErrorDisplay';
import { KYPSearchTabs } from './kyp/KYPSearchTabs';
import { HistoricalSearchViewer } from './kyp/HistoricalSearchViewer';
import { useAuth } from '../lib/auth.context';

/**
 * KYP Search Component
 * 
 * Implements a simplified state machine approach for the KYP search flow:
 * 1. Player search phase (search and select a player)
 * 2. Static data loading phase (load player profile, stats, etc.)
 * 3. Static data ready phase (display player data and enable KYP search)
 * 4. KYP search running phase (display search progress)
 * 5. KYP search complete phase (display search results)
 * 6. KYP search cancelled phase (show cancellation notice)
 */
export const KYPSearch = () => {
  // Use our simplified state management hook
  const [state, actions] = useKYPPageState();
  
  // Get auth context for token
  const { getToken } = useAuth();
  
  // Tab switching state
  const [activeTab, setActiveTab] = useState<'search' | 'history'>('search');
  const [selectedHistoricalSearch, setSelectedHistoricalSearch] = useState<number | null>(null);
  const [showHistoricalSearchDialog, setShowHistoricalSearchDialog] = useState<boolean>(false);
  const [historicalSearchData, setHistoricalSearchData] = useState<any>(null);
  
  // Handle viewing historical search results
  const handleViewHistoricalSearch = async (searchId: number) => {
    try {
      setSelectedHistoricalSearch(searchId);
      
      // Get the authentication token
      const token = getToken();
      if (!token) {
        throw new Error('Not authenticated');
      }
      
      // Import the API service
      const { fetchHistoricalSearchResults } = await import('../lib/services/kypApiService');
      
      // Fetch the historical search data
      const data = await fetchHistoricalSearchResults(searchId, token);
      
      // Check if the data was fetched successfully
      if (data.status === 'success') {
        setHistoricalSearchData(data);
        setShowHistoricalSearchDialog(true);
      } else if (data.status === 'pending') {
        throw new Error('Search is still in progress');
      } else {
        throw new Error('Failed to fetch search results');
      }
    } catch (error) {
      console.error('Error viewing historical search:', error);
      actions.dismissError();
      actions.setError(error instanceof Error ? error.message : 'Failed to fetch historical search data');
    }
  };
  
  // Handle closing historical search dialog
  const handleCloseHistoricalSearchDialog = () => {
    setShowHistoricalSearchDialog(false);
    setHistoricalSearchData(null);
  };
  
  // Render the appropriate phase component based on current phase
  const renderPhaseComponent = () => {
    switch (state.phase) {
      case 'player_search':
        return <PlayerSearchPhase state={state} actions={actions} />;
        
      case 'static_data_loading':
      case 'static_data_ready':
        return <StaticDataPhase state={state} actions={actions} />;
        
      case 'kyp_running':
      case 'kyp_complete':
      case 'kyp_cancelled':
        return <KYPSearchPhase state={state} actions={actions} />;
        
      default:
        return <PlayerSearchPhase state={state} actions={actions} />;
    }
  };
  
  return (
    <ErrorBoundary onRetry={() => window.location.reload()}>
      <div className="min-h-screen bg-black p-6">
        <div className="max-w-7xl mx-auto">
          <div className="bg-[#121212] rounded-lg shadow-lg p-6 text-[#CECFD2]">
            <div className="flex justify-between items-center mb-6">
              <h1 className="text-2xl font-bold text-[#CECFD2]">KYP Search</h1>
              
              {/* Tab Navigation */}
              <KYPSearchTabs activeTab={activeTab} setActiveTab={setActiveTab} />
            </div>
            
            {/* Error display */}
            <ErrorDisplay error={state.error} onDismiss={actions.dismissError} />
            
            {/* Content based on active tab */}
            {activeTab === 'search' ? (
              /* Search Tab Content - Phase-specific content */
              renderPhaseComponent()
            ) : (
              /* History Tab Content */
              <HistoricalSearchViewer 
                showHistoricalSearchDialog={showHistoricalSearchDialog}
                historicalSearchData={historicalSearchData}
                selectedHistoricalSearch={selectedHistoricalSearch}
                onViewResults={handleViewHistoricalSearch}
                onCloseDialog={handleCloseHistoricalSearchDialog}
              />
            )}
          </div>
        </div>
      </div>
    </ErrorBoundary>
  );
};
